/**
 * accountSuggestion.ts — the pure rule behind the L-4 near-match suggestion on
 * the contact surfaces (§25): when the rep leaves the Company field with
 * free text and NO pick, ask GET /v1/sales/matches?company= — the Slice C
 * chooser's read, reused AS-IS (the company branch runs the account trigram
 * at 0.45 over the tenant's accounts; no parameter was added) — and offer
 * "Use this account" on each candidate. Advisory only (C-2): it never gates
 * a save, and the email rule stays the only refusal on this path.
 */
import type { SalesMatchCandidate } from '../../../../api/types'
import type { AccountPick } from './AccountPicker'

/** What to query on blur, or '' for nothing: empty text and an already-made
 *  pick both ask nothing (a pick IS the answer; the picker's own type-ahead
 *  already showed the exact rows). */
export function suggestionQuery(value: AccountPick): string {
  if (value.accountId !== '') return ''
  return value.company.trim()
}

/** The pick a candidate row offers: the canonical name becomes the company
 *  text, exactly as the convert modal's "Use this account" does (C2.3). */
export function pickFromCandidate(c: SalesMatchCandidate): AccountPick {
  return { accountId: c.accountId ?? c.id, company: c.accountName || c.displayName }
}

/** The tile's three honest states, worded for a company (the email tile has
 *  its own words). */
export const ACCOUNT_SUGGESTION_COPY = {
  // The eyebrow names what the tile IS — a suggestion, not a warning — and
  // reads correctly over every state: a candidate row, "Checking…",
  // "unavailable", and "No account has a similar name…". ("Suggested account"
  // over "No account has a similar name" would be the same contradiction the
  // inherited "Possible duplicates" was.)
  heading: 'Account suggestion',
  checking: 'Checking for an account with a similar name…',
  unavailable: 'Account check unavailable — you can still save without a link.',
  empty: 'No account has a similar name. Pick one from the search, or leave the contact unlinked.',
} as const
